/* r11 item 1 (AJ 2026-10-10: "still cant scroll on my screen when i close out of the configure or preview"; "why does the
   scroll for the actual window itself make it like blank ... can u just make it look normal like as if its a popup").

   One scroll lock for every overlay (Configure, compare, side cart, policy drawers, search, phone menu, photo viewer,
   theme drawers). Each overlay used to save and restore the page's own overflow; when two overlapped (or Shopify's Shop
   component restored a value it had saved while a popup was open) the page was left with overflow: hidden and could not
   scroll. And overflow: hidden on the page, with the page's stable scrollbar gutter, left an empty strip where the
   scrollbar had been.

   This lock never touches the page's overflow, so the page's scrollbar stays exactly as it is and nothing moves sideways.
   While at least one owner holds the lock, scrolling the page behind is stopped at the input: wheel and trackpad, touch,
   and the scroll keys are cancelled unless they would scroll something inside an open overlay; overlay scroll areas keep
   overscroll-behavior: contain so a fast scroll does not carry on to the page. On a mouse-and-keyboard screen a drag of the
   page's scrollbar is put back.

   API (window.cqScrollLock):
     lock(owner, { root, isOpen })  owner: the overlay element (or any key); root: element or elements inside which
                                    scrolling is allowed (default: owner); isOpen(): whether the overlay is still open.
     unlock(owner)                  the page unlocks when no owner is left.
     owners()                       for tests: names of the current owners.
   Never stuck: owners whose isOpen() is false, or whose element left the page, are dropped before every check, on
   pageshow, on back and forward (popstate) and on hash changes. */
(() => {
  if (window.cqScrollLock) return;
  const html = document.documentElement;
  const owners = new Map();
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const KEYS = { ' ': 1, Spacebar: 1, PageUp: -1, PageDown: 1, Home: -1, End: 1, ArrowUp: -1, ArrowDown: 1, ArrowLeft: -1, ArrowRight: 1 };
  const SCROLLS = /^(auto|scroll|overlay)$/;
  let x = 0;
  let y = 0;
  let touch = null;
  let pointerPath = null;
  let keyHold = 0;
  let on = false;

  const alive = (owner, entry) => {
    if (owner instanceof Node && !owner.isConnected) return false;
    try {
      return entry.isOpen ? Boolean(entry.isOpen()) : true;
    } catch (_) {
      return false;
    }
  };

  // Can this element scroll by (dx, dy)? The page itself (html, body) never counts.
  const canScroll = (element, dx, dy) => {
    if (!(element instanceof Element) || element === html || element === document.body) return false;
    const style = getComputedStyle(element);
    if (dy && SCROLLS.test(style.overflowY) && element.scrollHeight - element.clientHeight > 1) {
      if (dy < 0 ? element.scrollTop > 0.5 : element.scrollTop + element.clientHeight < element.scrollHeight - 0.5) return true;
    }
    if (dx && SCROLLS.test(style.overflowX) && element.scrollWidth - element.clientWidth > 1) {
      const range = element.scrollWidth - element.clientWidth;
      const rtl = style.direction === 'rtl';
      const min = rtl ? -range : 0;
      const max = rtl ? 0 : range;
      if (dx > 0 ? element.scrollLeft < max - 0.5 : element.scrollLeft > min + 0.5) return true;
    }
    return false;
  };

  const roots = () => {
    const set = new Set();
    owners.forEach((entry) => entry.roots.forEach((root) => set.add(root)));
    return set;
  };

  // A scroll is allowed only when something between the target and the overlay's root can take it. Shopify's
  // cancellation policy modal (top-level-modal, appended to the body) counts as an overlay too.
  const allowed = (path, dx, dy) => {
    if (!dx && !dy) return true;
    const open = roots();
    const end = path.findIndex((node) => open.has(node) || node.nodeName === 'TOP-LEVEL-MODAL');
    if (end < 0) return false;
    for (let i = 0; i <= end; i += 1) if (canScroll(path[i], dx, dy)) return true;
    return false;
  };

  // keep: an owner locking right now, whose overlay opens later in the same task (lock() may come before showModal()).
  const prune = (keep) => {
    owners.forEach((entry, owner) => { if (owner !== keep && !alive(owner, entry)) owners.delete(owner); });
    sync();
    return owners.size > 0;
  };

  const onWheel = (event) => {
    if (!prune() || event.ctrlKey) return;
    let dx = event.deltaX;
    let dy = event.deltaY;
    if (event.shiftKey && !dx) { dx = dy; dy = 0; }
    const vertical = Math.abs(dy) >= Math.abs(dx);
    if (!allowed(event.composedPath(), vertical ? 0 : dx, vertical ? dy : 0)) event.preventDefault();
  };

  const onTouchStart = (event) => {
    touch = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
  };

  const onTouchMove = (event) => {
    if (!touch || event.touches.length !== 1 || !event.cancelable || !prune()) return;
    const target = event.composedPath()[0];
    if (target instanceof Element && target.closest('input[type="range"]')) return;
    const dx = touch.x - event.touches[0].clientX;
    const dy = touch.y - event.touches[0].clientY;
    const vertical = Math.abs(dy) >= Math.abs(dx);
    if (!allowed(event.composedPath(), vertical ? 0 : dx, vertical ? dy : 0)) event.preventDefault();
  };

  const onPointerDown = (event) => { pointerPath = event.composedPath(); };

  // Does a form field use this key itself? A key a field does not use (Page Down anywhere, End with the caret already at
  // the end or in an empty field) is passed on by the browser to scroll the page, so it goes through the check below.
  const fieldTakes = (field, key) => {
    if (field.isContentEditable || field.localName === 'select') return true;
    const type = field.localName === 'input' ? field.type || 'text' : 'textarea';
    if (type === 'range') return true;
    if (key === ' ' || key === 'Spacebar') return true;
    if (key === 'PageUp' || key === 'PageDown') return false;
    if (type === 'checkbox' || type === 'radio') return type === 'radio' && key.startsWith('Arrow');
    if (type === 'number') return key === 'ArrowUp' || key === 'ArrowDown';
    let start;
    let end;
    try {
      start = field.selectionStart;
      end = field.selectionEnd;
    } catch (_) {
      return true;
    }
    if (start == null) return true;
    if (start !== end) return true;
    if (key === 'Home' || key === 'ArrowUp' || key === 'ArrowLeft') return start > 0;
    return end < field.value.length;
  };

  const onKey = (event) => {
    if (!(event.key in KEYS) || event.defaultPrevented || !prune()) return;
    const target = event.composedPath()[0];
    if (target instanceof Element) {
      const field = target.isContentEditable ? target : target.closest('input, textarea, select');
      if (field && fieldTakes(field, event.key)) {
        // Some browsers (phones with a keyboard) still pass Home or End on to the page; if the page moves, put it back.
        keyHold = performance.now() + 600;
        return;
      }
      const space = event.key === ' ' || event.key === 'Spacebar';
      if (space && target.closest('button, summary, [role="button"], [role="checkbox"], [role="radio"], [role="switch"], [role="tab"], [role="menuitem"], [role="option"]')) return;
      if (!space && target.closest('[role="slider"], [role="tablist"], [role="radiogroup"], [role="listbox"], [role="menu"], [role="menubar"], [role="grid"], [role="tree"], [role="spinbutton"]')) return;
    }
    const horizontal = event.key === 'ArrowLeft' || event.key === 'ArrowRight';
    const sign = event.key === ' ' || event.key === 'Spacebar' ? (event.shiftKey ? -1 : 1) : KEYS[event.key];
    // With focus on the page itself, the browser scrolls what was last clicked.
    const path = target === document.body || target === html || target === document ? pointerPath || event.composedPath() : event.composedPath();
    if (!allowed(path, horizontal ? sign : 0, horizontal ? 0 : sign)) event.preventDefault();
  };

  // A drag of the page's own scrollbar (mouse screens only; phones have no draggable bar) is put back, as is a page
  // scroll that follows a key a form field let through.
  const onScroll = () => {
    if (!on || !(fine.matches || performance.now() < keyHold) || !prune()) return;
    if (Math.abs(window.scrollY - y) > 0.5 || Math.abs(window.scrollX - x) > 0.5) window.scrollTo({ left: x, top: y, behavior: 'instant' });
  };

  const listeners = [
    [window, 'wheel', onWheel, { passive: false, capture: true }],
    [window, 'touchstart', onTouchStart, { passive: true, capture: true }],
    [window, 'touchmove', onTouchMove, { passive: false, capture: true }],
    [window, 'keydown', onKey, { capture: true }],
    [window, 'pointerdown', onPointerDown, { passive: true, capture: true }],
    [window, 'scroll', onScroll, { passive: true }],
  ];

  function sync() {
    const want = owners.size > 0;
    if (want === on) return;
    on = want;
    if (on) {
      x = window.scrollX;
      y = window.scrollY;
      listeners.forEach(([target, type, handler, options]) => target.addEventListener(type, handler, options));
      // A CSS hook only (scroll anchoring off, so the page behind never moves while it is covered); never overflow.
      html.setAttribute('data-cq-scroll-locked', '');
    } else {
      listeners.forEach(([target, type, handler, options]) => target.removeEventListener(type, handler, options));
      html.removeAttribute('data-cq-scroll-locked');
      touch = null;
      pointerPath = null;
    }
  }

  window.cqScrollLock = {
    lock(owner, options = {}) {
      if (!owner) return;
      const root = options.root || (owner instanceof Element ? owner : null);
      const list = (Array.isArray(root) ? root : [root]).filter(Boolean);
      owners.set(owner, { roots: list, isOpen: typeof options.isOpen === 'function' ? options.isOpen : null });
      prune(owner);
    },
    unlock(owner) {
      owners.delete(owner);
      prune();
    },
    refresh: () => prune(),
    owners() {
      prune();
      return [...owners.keys()].map((owner) => (owner instanceof Element ? owner.id || owner.localName : String(owner)));
    },
  };

  ['pageshow', 'popstate', 'hashchange'].forEach((type) => window.addEventListener(type, () => prune()));
})();
